import { useState } from 'react';
import { BarChart3, Clock3, Sparkles, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { EmptyState } from '@components/common/EmptyState';
import { SectionCard } from '@components/common/SectionCard';
import { StatCard } from '@components/common/StatCard';
import { useCRMDashboard, useFollowUps, useWorkQueue } from '@hooks/useCRM';
import { useCurrentUser } from '@hooks/useAuth';
import { formatDate, formatRelativeDate } from '@lib/format';

export function DashboardPage() {
  const [myView, setMyView] = useState(true);
  const navigate = useNavigate();
  const { data: currentUser } = useCurrentUser();
  const ownerUserId = myView ? currentUser?.id : undefined;
  const { data: summary, isLoading: isSummaryLoading } = useCRMDashboard();
  const { data: followUps, isLoading: isFollowUpsLoading } = useFollowUps(ownerUserId);
  const { data: workQueue, isLoading: isWorkQueueLoading } = useWorkQueue();

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold text-content-primary mb-2">Dashboard</h1>
        <p className="text-content-secondary">Welcome to Founder CRM</p>
      </div>

      <section className="dashboard-filter-bar">
        <div className="toggle-group">
          <button
            type="button"
            className={`button ${myView ? 'button-primary' : 'button-secondary'}`}
            onClick={() => setMyView(true)}
          >
            Mine
          </button>
          <button
            type="button"
            className={`button ${!myView ? 'button-primary' : 'button-secondary'}`}
            onClick={() => setMyView(false)}
          >
            All
          </button>
        </div>
      </section>

      <section className="stats-grid">
        <StatCard
          label="Accounts"
          value={isSummaryLoading ? '...' : summary?.accounts ?? 0}
          detail="Companies being researched, sold, or served"
          accent="gold"
          icon={<Users size={18} />}
        />
        <StatCard
          label="Leads"
          value={isSummaryLoading ? '...' : summary?.leads ?? 0}
          detail="People or prospects currently in motion"
          accent="teal"
          icon={<Sparkles size={18} />}
        />
        <StatCard
          label="Opportunities"
          value={isSummaryLoading ? '...' : summary?.opportunities ?? 0}
          detail="Pipeline items tied to revenue conversations"
          accent="rose"
          icon={<BarChart3 size={18} />}
        />
        <StatCard
          label="Follow-ups Due"
          value={isSummaryLoading ? '...' : summary?.follow_ups_due ?? 0}
          detail="Leads that need a nudge today"
          accent="ink"
          icon={<Clock3 size={18} />}
        />
      </section>

      <div className="dashboard-columns">
        <SectionCard
          title="Today's Work Queue"
          subtitle={workQueue?.generated_at
            ? `Queue refreshed ${formatRelativeDate(workQueue.generated_at)}`
            : 'AI-ranked leads ready to work'
          }
        >
          {isWorkQueueLoading ? (
            <div className="loading-row">Loading work queue…</div>
          ) : workQueue?.leads.length ? (
            <div className="stack-list">
              {workQueue.leads.map((lead) => (
                <article
                  key={lead.id}
                  className="stack-item clickable-row"
                  onClick={() => navigate(`/leads?leadId=${lead.id}`)}
                >
                  <div>
                    <strong>
                      {[lead.first_name, lead.last_name].filter(Boolean).join(' ') || lead.email || 'Unnamed lead'}
                    </strong>
                    <p>{lead.company_name || 'No company'}</p>
                  </div>
                  <div className="ai-score-cluster">
                    {lead.ai_score != null && (
                      <span className={`pill ${lead.ai_score >= 70 ? 'pill-high' : lead.ai_score >= 40 ? 'pill-medium' : 'pill-low'}`}>
                        {Math.round(lead.ai_score)}
                      </span>
                    )}
                    {lead.ai_next_action && (
                      <span className="pill pill-neutral">{lead.ai_next_action}</span>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState
              title="Queue is being built"
              body="Add your first lead or wait for the nightly AI refresh."
            />
          )}
        </SectionCard>

        <SectionCard
          title="Follow-ups Queue"
          subtitle="Everything already overdue or due now."
        >
          {isFollowUpsLoading ? (
            <div className="loading-row">Loading follow-ups…</div>
          ) : followUps?.leads.length || followUps?.tasks.length ? (
            <div className="stack-list">
              {followUps?.leads.map((lead) => (
                <article key={lead.id} className="stack-item">
                  <div>
                    <strong>{lead.company_name || lead.email || 'Lead follow-up'}</strong>
                    <p>Lead • due {formatDate(lead.follow_up_at)}</p>
                  </div>
                  <span>{formatRelativeDate(lead.follow_up_at)}</span>
                </article>
              ))}
              {followUps?.tasks.map((task) => (
                <article key={task.id} className="stack-item">
                  <div>
                    <strong>{task.title}</strong>
                    <p>{task.task_type} • {task.status}</p>
                  </div>
                  <span>{formatRelativeDate(task.due_at)}</span>
                </article>
              ))}
            </div>
          ) : (
            <EmptyState title="Nothing due" body="Tasks and lead follow-ups will appear here as soon as they are scheduled." />
          )}
        </SectionCard>
      </div>
    </div>
  );
}
